import { useEffect, useRef } from 'react';
import { captureEvent } from '../utils/posthog';
import { EVENTS, PROPERTIES } from '../utils/analytics';

/**
 * Hook to track section visibility and time spent
 * @param {string} sectionName - Name of the section
 * @param {string} sectionId - ID of the section element
 * @param {object} options - Options for tracking
 */
export const useSectionTracking = (sectionName, sectionId, options = {}) => {
  const sectionRef = useRef(null);
  const timeEnteredRef = useRef(null);
  const hasTrackedViewRef = useRef(false);
  const observerRef = useRef(null);

  useEffect(() => {
    const element = document.getElementById(sectionId);
    if (!element) {
      console.warn(`Section element with id "${sectionId}" not found`);
      return;
    }

    sectionRef.current = element;

    // Create Intersection Observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTrackedViewRef.current) {
            // Section entered viewport
            timeEnteredRef.current = Date.now();
            hasTrackedViewRef.current = true;

            // Calculate scroll depth
            const scrollDepth = calculateScrollDepth();

            captureEvent(EVENTS.SECTION_VIEWED, {
              [PROPERTIES.SECTION_NAME]: sectionName,
              [PROPERTIES.SECTION_ID]: sectionId,
              [PROPERTIES.SCROLL_DEPTH]: scrollDepth,
              viewport_height: window.innerHeight,
              page_height: document.documentElement.scrollHeight,
            });
          } else if (!entry.isIntersecting && hasTrackedViewRef.current) {
            // Section left viewport - track time spent
            if (timeEnteredRef.current) {
              const timeSpent = Math.round(
                (Date.now() - timeEnteredRef.current) / 1000
              );

              captureEvent(EVENTS.SECTION_VIEWED, {
                [PROPERTIES.SECTION_NAME]: sectionName,
                [PROPERTIES.SECTION_ID]: sectionId,
                [PROPERTIES.TIME_ON_SECTION]: timeSpent,
                action: 'section_exited',
              });

              timeEnteredRef.current = null;
            }
          }
        });
      },
      {
        threshold: options.threshold || 0.3, // Trigger when 30% visible
        rootMargin: options.rootMargin || '0px',
      }
    );

    observer.observe(element);
    observerRef.current = observer;

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [sectionName, sectionId, options.threshold, options.rootMargin]);

  // Calculate scroll depth percentage
  const calculateScrollDepth = () => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollDepth = Math.round(
      ((scrollTop + windowHeight) / documentHeight) * 100
    );
    return Math.min(scrollDepth, 100);
  };

  return sectionRef;
};

